/**
 * 要望を出す先（画面）の選択肢。
 *
 * **選択肢は `PILAB_AREAS` から作る。** ここに画面名を並べると、メニューが増えたときに
 * 出せる先が増えない（手で足すまで気付けない）。
 *
 * 表示名の引き方が 2 通りあるのは、**要望の対象がメニューより広い**から。メニューに
 * 出ている画面はサイドバーと同じ `nav.<key>` を使い、メニューに無い場所（他の画面の中の
 * タブ、横断の話）は `improvements.areas.<key>` を使う。
 *
 * **見られない画面は選択肢に出さない。** 出せてしまうと、その画面を知らない人が
 * 当て推量で要望を出すことになり、こちらも要望の中身を本人に確認できない。絞り込みは
 * `visiblePilabAreas`（画面・API 共通の判定）に委ねる——ここで条件を書くと API とずれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望の対象画面の選択肢を、画面の識別子の正本から機械的に作り、見られる画面だけに絞る
 * @graph-connects PILAB_AREAS [references] 画面の識別子の正本
 * @graph-connects visiblePilabAreas [calls] 見られる画面だけに絞る共通判定
 */

import { PILAB_AREAS, isPilabNavArea, type PilabArea } from '@cortex/core-types';
import {
  visiblePilabAreas,
  type PageAccessContext,
} from '@cortex/domain-product-innovation-lab-types';

/** 選択肢 1 つ。 @graph-connects none */
export interface AreaOption {
  value: PilabArea;
  label: string;
}

/** 1 件の画面名を引く。 @graph-connects none */
export function areaLabel(area: PilabArea, translate: (key: string) => string): string {
  return isPilabNavArea(area) ? translate(`nav.${area}`) : translate(`improvements.areas.${area}`);
}

/** その人が見られる画面の選択肢を作る。 @graph-connects none */
export function buildAreaOptions(
  translate: (key: string) => string,
  access: PageAccessContext,
): AreaOption[] {
  return visiblePilabAreas(PILAB_AREAS, access).map((area) => ({
    value: area,
    label: areaLabel(area, translate),
  }));
}
